import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePaypalSubscription from "../../api/usePaypalSubscription.js";
import { commonErrorToastMessage, commonSuccessToastMessage } from "../../components/toastify/AllToastifyErrors.js";

const PLAN_VALIDITY_MAP = { 1: 1, 3: 3, 12: 12 };

// Adjust if your login route is different (e.g. "/" or "/login")
const LOGIN_ROUTE = "/";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchPaymentResult } = usePaypalSubscription();

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"

  const redirectToLogin = (delay = 2500) => {
    setTimeout(() => {
      localStorage.clear(); // force fresh login so plan is re-fetched from server
      navigate('/');
    }, delay);
  };

  useEffect(() => {
    const token = searchParams.get("subscription_id") || searchParams.get("token");

    if (!token) {
      setStatus("error");
      commonErrorToastMessage("No subscription token found in the URL.");
      return;
    }

    const verify = async () => {
      const result = await fetchPaymentResult(token);

      // Network / server error
      if (!result) {
        setStatus("error");
        return;
      }

      // 202 pending — PayPal payment received but webhook hasn't written DB yet.
      // This is the normal case. Payment IS successful; we just redirect to login.
      if (result.pending) {
        commonSuccessToastMessage(
          "Payment received! Please log in again to activate your new plan."
        );
        setStatus("success");
        redirectToLogin();
        return;
      }

      // Webhook already fired before the redirect (rare but possible)
      if (result.success) {
        localStorage.setItem("subscriptionPlan", PLAN_VALIDITY_MAP[result.planValidity] ?? result.planValidity);
        localStorage.setItem("monthEndDate", result.expiryDate);
        localStorage.removeItem("UserAttempts");

        commonSuccessToastMessage(
          `Subscription activated! Your plan is valid until ${result.expiryDate}.`
        );
        setStatus("success");
        redirectToLogin();
        return;
      }

      setStatus("error");
      commonErrorToastMessage("Payment verification failed. Please contact support.");
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-[#212529] px-4">

      {status === "loading" && (
        <div className="text-center">
          <span className="animate-spin inline-block w-10 h-10 border-4 border-[#f4833d] border-t-transparent rounded-full mb-4" />
          <p className="text-lg font-medium">Verifying your payment…</p>
        </div>
      )}

      {status === "success" && (
        <div className="text-center">
          <i className="fa-solid fa-circle-check text-green-500 text-5xl mb-4" />
          <h1 className="text-2xl font-semibold">Payment Received!</h1>
          <p className="text-gray-500 mt-2">
            Please log in again to activate your new plan.
          </p>
          <p className="text-sm text-gray-400 mt-1">Redirecting to login…</p>
        </div>
      )}

      {status === "error" && (
        <div className="text-center">
          <i className="fa-solid fa-circle-exclamation text-red-500 text-5xl mb-4" />
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-gray-500 mt-2">
            If you completed the payment, check your email for a PayPal confirmation
            and log in again — your plan may already be active.
          </p>
          <button
            className="mt-6 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] transition-all"
            onClick={() => {
              localStorage.clear();
              navigate(LOGIN_ROUTE);
            }}
          >
            Go to Login
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;